/**
 * In-memory mock backend.
 *
 * Implements the same authorization algorithm as the real server
 * (docs/05-security-architecture.md §1.1) so that permission-aware UI can be built and
 * demonstrated end to end. Every function here has a one-to-one counterpart in
 * docs/02-api-architecture.md §4.
 */

import * as seed from './data';
import { getTypeGroup, getExtension } from '../utils/fileTypes';
import { escapeRegExp } from '../utils/format';

// Mirrors the server's extractor registry (server/src/adapters/ocr/extractors): these
// formats carry a text layer that is read in-process; images need an OCR engine.
const TEXT_LAYER_TYPES = ['txt', 'md', 'markdown', 'csv', 'tsv', 'log', 'json', 'xml', 'yaml', 'yml', 'html', 'htm', 'xlsx', 'xlsm', 'xls', 'ods', 'docx', 'pptx', 'pdf'];
const IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'bmp', 'gif', 'webp'];

const db = {
  organization: seed.organization,
  permissions: seed.permissions,
  roles: seed.roles.map((r) => ({ ...r, permissions: [...r.permissions] })),
  departments: [...seed.departments],
  users: [...seed.users],
  folders: [...seed.folders],
  documents: [...seed.documents],
  versions: [...seed.documentVersions],
  shares: [...seed.shares],
  links: [...seed.externalLinks],
  favorites: [...seed.favorites],
  recent: [...seed.recentItems],
  notifications: [...seed.notifications],
  audit: [...seed.auditLogs],
  settings: JSON.parse(JSON.stringify(seed.systemSettings)),
};

let sessionUserId = 'usr-vaisakh';
let idCounter = 1000;
const nextId = (prefix) => `${prefix}-${(idCounter += 1)}`;
const nowIso = () => new Date().toISOString();

const delay = (ms = 160) => new Promise((r) => setTimeout(r, ms));

/* ============================================================ helpers/lookups */

const byId = (list, id) => list.find((x) => x.id === id) || null;

export const getUser = (id) => byId(db.users, id);
export const getDepartment = (id) => byId(db.departments, id);
export const getRole = (id) => byId(db.roles, id);

function publicUser(user) {
  if (!user) return null;
  const dept = getDepartment(user.departmentId);
  const role = getRole(user.roleId);
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    jobTitle: user.jobTitle,
    status: user.status,
    mfaEnabled: user.mfaEnabled,
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
    preferences: user.preferences || {},
    department: dept ? { id: dept.id, name: dept.name, color: dept.color, code: dept.code } : null,
    role: role ? { id: role.id, key: role.key, name: role.name, rank: role.rank } : null,
  };
}

export function permissionsOf(user) {
  const role = getRole(user?.roleId);
  return new Set(role ? role.permissions : []);
}

/* ================================================== authorization (the core) */

const OWNER_CAPS = ['view', 'download', 'edit', 'replace', 'share', 'delete', 'manage'];
const DEPT_MEMBER_CAPS = ['view', 'download'];

/** capability -> the role permission that acts as its ceiling */
const CAP_CEILING = {
  view: 'DOCUMENT_VIEW',
  download: 'DOCUMENT_DOWNLOAD',
  edit: 'DOCUMENT_EDIT',
  replace: 'DOCUMENT_REPLACE',
  delete: 'DOCUMENT_DELETE',
  share: 'DOCUMENT_SHARE',
};

function shareGrantsFor(user, documentId) {
  const role = getRole(user.roleId);
  const caps = [];
  db.shares.forEach((s) => {
    if (s.documentId !== documentId || s.revokedAt) return;
    if (s.expiresAt && new Date(s.expiresAt).getTime() < Date.now()) return;
    const matches =
      (s.principalType === 'user' && s.principalId === user.id) ||
      (s.principalType === 'department' && s.principalId === user.departmentId) ||
      (s.principalType === 'role' && role && s.principalId === role.id);
    if (matches) caps.push(...s.capabilities);
  });
  return caps;
}

/**
 * Effective capabilities of `user` on `doc`.
 * Returns [] when the user has no access at all — callers treat that as "does not exist".
 */
export function capabilitiesFor(user, doc) {
  if (!user || !doc || user.status !== 'active') return [];
  const perms = permissionsOf(user);
  const role = getRole(user.roleId);
  const grants = new Set();

  if (doc.ownerId === user.id) OWNER_CAPS.forEach((c) => grants.add(c));

  if (doc.departmentId === user.departmentId) {
    DEPT_MEMBER_CAPS.forEach((c) => grants.add(c));
    if (role && (role.key === 'DEPARTMENT_MANAGER' || role.key === 'MANAGER')) {
      OWNER_CAPS.forEach((c) => grants.add(c));
    }
  }

  shareGrantsFor(user, doc.id).forEach((c) => grants.add(c));

  if (perms.has('ADMIN_DOCUMENT_OVERRIDE')) OWNER_CAPS.forEach((c) => grants.add(c));

  if (grants.size === 0) return [];

  // Intersect with the role ceiling: a share can never exceed what the role allows.
  return [...grants].filter((cap) => {
    const required = CAP_CEILING[cap];
    if (!required) return doc.ownerId === user.id || perms.has('ADMIN_DOCUMENT_OVERRIDE');
    return perms.has(required);
  });
}

export function capabilitiesForFolder(user, folder) {
  if (!user || !folder) return [];
  const perms = permissionsOf(user);
  const role = getRole(user.roleId);
  const caps = new Set();
  const sameDept = folder.departmentId === user.departmentId;
  const isOwner = folder.ownerId === user.id;
  const isAdmin = perms.has('ADMIN_DOCUMENT_OVERRIDE');

  if (sameDept || isOwner || isAdmin) caps.add('view');
  if (isOwner || isAdmin || (sameDept && role && ['DEPARTMENT_MANAGER', 'MANAGER'].includes(role.key))) {
    ['edit', 'delete', 'share', 'download', 'manage'].forEach((c) => caps.add(c));
  } else if (sameDept) {
    caps.add('download');
  }
  if (caps.size === 0) return [];
  return [...caps].filter((cap) => {
    const required = CAP_CEILING[cap];
    if (!required) return isOwner || isAdmin;
    return perms.has(required);
  });
}

const canView = (user, doc) => capabilitiesFor(user, doc).includes('view');

/* ==================================================== decoration for the API */

function folderPath(folderId) {
  const chain = [];
  let current = byId(db.folders, folderId);
  let guard = 0;
  while (current && guard < 25) {
    chain.unshift(current);
    current = current.parentId ? byId(db.folders, current.parentId) : null;
    guard += 1;
  }
  return chain;
}

function decorateDocument(doc, user) {
  const owner = getUser(doc.ownerId);
  const dept = getDepartment(doc.departmentId);
  const path = folderPath(doc.folderId);
  return {
    id: doc.id,
    type: 'document',
    name: doc.name,
    extension: doc.extension,
    sizeBytes: doc.sizeBytes,
    folderId: doc.folderId,
    folderName: path[path.length - 1]?.name || '',
    path: path.map((f) => f.name).join(' / '),
    pathSegments: path.map((f) => ({ id: f.id, name: f.name })),
    department: dept ? { id: dept.id, name: dept.name, color: dept.color } : null,
    owner: owner ? { id: owner.id, name: owner.name, email: owner.email } : null,
    versionCount: doc.versionCount,
    currentVersionNo: doc.currentVersionNo,
    tags: doc.tags,
    description: doc.description,
    isConfidential: doc.isConfidential,
    ocrStatus: doc.ocrStatus,
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
    deletedAt: doc.deletedAt,
    deletedBy: doc.deletedBy ? publicUser(getUser(doc.deletedBy)) : null,
    purgeAfter: doc.purgeAfter || null,
    isFavorite: db.favorites.some(
      (f) => f.userId === user.id && f.resourceType === 'document' && f.resourceId === doc.id,
    ),
    shareCount: db.shares.filter((s) => s.documentId === doc.id && !s.revokedAt).length,
    hasExternalLink: db.links.some(
      (l) => l.documentId === doc.id && !l.revokedAt && new Date(l.expiresAt).getTime() > Date.now(),
    ),
    capabilities: capabilitiesFor(user, doc),
  };
}

function decorateFolder(folder, user) {
  const owner = getUser(folder.ownerId);
  const dept = getDepartment(folder.departmentId);
  const childDocs = db.documents.filter((d) => d.folderId === folder.id && !d.deletedAt);
  const childFolders = db.folders.filter((f) => f.parentId === folder.id && !f.deletedAt);
  return {
    id: folder.id,
    type: 'folder',
    name: folder.name,
    parentId: folder.parentId,
    department: dept ? { id: dept.id, name: dept.name, color: dept.color } : null,
    owner: owner ? { id: owner.id, name: owner.name } : null,
    itemCount: childDocs.length + childFolders.length,
    sizeBytes: null,
    createdAt: folder.createdAt,
    updatedAt: folder.updatedAt,
    isFavorite: db.favorites.some(
      (f) => f.userId === user.id && f.resourceType === 'folder' && f.resourceId === folder.id,
    ),
    capabilities: capabilitiesForFolder(user, folder),
  };
}

/* ================================================================== auditing */

function audit(action, { resourceType = 'document', resourceId = null, resourceName = null, metadata = {}, result = 'success' } = {}) {
  const actor = getUser(sessionUserId);
  db.audit.unshift({
    id: nextId('aud'),
    actorId: actor?.id,
    actorName: actor?.name,
    actorEmail: actor?.email,
    action,
    resourceType,
    resourceId,
    resourceName,
    departmentId: actor?.departmentId,
    result,
    ipAddress: '10.18.1.24',
    userAgent: 'Chrome 129 / Windows',
    createdAt: nowIso(),
    metadata,
  });
}

class MockError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function requirePermission(user, key) {
  if (!permissionsOf(user).has(key)) {
    audit('PERMISSION_DENIED', { resourceType: 'permission', resourceName: key, result: 'denied' });
    throw new MockError(403, 'FORBIDDEN', 'You do not have permission to perform this action.');
  }
}

function requireCapability(user, doc, cap) {
  const caps = capabilitiesFor(user, doc);
  if (!caps.length) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
  if (!caps.includes(cap)) {
    audit('PERMISSION_DENIED', { resourceId: doc.id, resourceName: doc.name, metadata: { capability: cap }, result: 'denied' });
    throw new MockError(403, 'FORBIDDEN', 'You do not have permission to perform this action on this document.');
  }
}

/**
 * Build a result snippet around a match, snapped to word boundaries so it never opens
 * mid-word. Postgres does this with ts_headline; this mirrors the output shape.
 */
function makeSnippet(text, index, matchLength, before = 70, after = 90) {
  if (!text) return '';
  let start = Math.max(0, index - before);
  if (start > 0) {
    const space = text.indexOf(' ', start);
    if (space > -1 && space < index) start = space + 1;
  }
  let end = Math.min(text.length, index + matchLength + after);
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > index + matchLength) end = space;
  }
  const body = text.slice(start, end).replace(/\s+/g, ' ').trim();
  return `${start > 0 ? '…' : ''}${body}${end < text.length ? '…' : ''}`;
}

/* ================================================================== sorting */

function sortItems(items, sort = 'name:asc') {
  const [field, dir = 'asc'] = sort.split(':');
  const mul = dir === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => {
    // folders always above documents
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    let av;
    let bv;
    switch (field) {
      case 'modified':
        av = new Date(a.updatedAt).getTime();
        bv = new Date(b.updatedAt).getTime();
        break;
      case 'size':
        av = a.sizeBytes || 0;
        bv = b.sizeBytes || 0;
        break;
      case 'owner':
        av = a.owner?.name || '';
        bv = b.owner?.name || '';
        break;
      default:
        av = a.name.toLowerCase();
        bv = b.name.toLowerCase();
    }
    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
    return 0;
  });
}

/* ============================================================= API surface */

export const mockApi = {
  /* ---------------------------------------------------------------- auth */
  async login({ email, password }) {
    await delay(420);
    const user = db.users.find((u) => u.email.toLowerCase() === String(email).toLowerCase().trim());
    if (!user || !password) {
      throw new MockError(401, 'INVALID_CREDENTIALS', 'Incorrect email or password.');
    }
    if (user.status === 'suspended' || user.status === 'deactivated') {
      throw new MockError(403, 'ACCOUNT_DISABLED', 'This account has been disabled. Contact your administrator.');
    }
    if (user.mfaEnabled) {
      return {
        mfaRequired: true,
        challengeId: `chl-${user.id}`,
        method: 'whatsapp',
        maskedDestination: '+•••••••5916',
        expiresInSeconds: 300,
        resendAfterSeconds: 60,
        devCode: '123456',
      };
    }
    sessionUserId = user.id;
    user.lastLoginAt = nowIso();
    audit('USER_LOGIN', { resourceType: 'session' });
    return { mfaRequired: false, user: publicUser(user) };
  },

  async resendMfa(challengeId) {
    await delay(300);
    return { challengeId, method: 'whatsapp', maskedDestination: '+•••••••5916', resendAfterSeconds: 60, devCode: '123456' };
  },

  async crmPreview() {
    await delay(500);
    requirePermission(getUser(sessionUserId), 'ADMIN_USER_MANAGE');
    return {
      configured: false,
      total: 0,
      create: [],
      update: [],
      skip: [],
      deactivate: [],
    };
  },

  async crmSync() {
    await delay(400);
    throw new MockError(503, 'CRM_NOT_CONFIGURED', 'The CRM is only reachable from the live API, not in demo mode.');
  },

  async resetDemoData() {
    await delay(400);
    throw new MockError(503, 'NOT_AVAILABLE', 'Demonstration data can only be reset against the live API.');
  },

  async mfaStatus() {
    await delay(150);
    const user = getUser(sessionUserId);
    return {
      method: 'totp',
      authenticatorEnrolled: Boolean(user?.mfaEnabled),
      mfaEnabled: Boolean(user?.mfaEnabled),
      phoneVerified: Boolean(user?.phoneVerified),
      recoveryCodes: { remaining: user?.mfaEnabled ? 10 : 0, total: user?.mfaEnabled ? 10 : 0 },
    };
  },

  async resetAuthenticator() {
    await delay(300);
    const user = getUser(sessionUserId);
    if (user) user.mfaEnabled = false;
    return { ok: true };
  },

  async regenerateRecoveryCodes() {
    await delay(300);
    return { recoveryCodes: Array.from({ length: 10 }, (_, i) => `DEMO${String(i).padStart(1, '0')}-CODE${i}`) };
  },

  async startPhoneVerification(phone) {
    await delay(400);
    return { challengeId: 'chl-phone', maskedDestination: `+${'•'.repeat(Math.max(0, phone.length - 4))}${phone.slice(-4)}`, resendAfterSeconds: 60, devCode: '123456' };
  },

  async confirmPhoneVerification({ code, phone }) {
    await delay(320);
    if (!/^\d{6}$/.test(String(code || ''))) throw new MockError(401, 'INVALID_CODE', 'That code is not correct.');
    const user = getUser(sessionUserId);
    if (user) {
      user.phone = phone;
      user.phoneVerified = true;
    }
    return publicUser(user);
  },

  async verifyMfa({ challengeId, code }) {
    await delay(380);
    if (!/^\d{6}$/.test(String(code || ''))) {
      throw new MockError(401, 'INVALID_CODE', 'That code is not correct.');
    }
    const userId = String(challengeId).replace('chl-', '');
    const user = getUser(userId);
    if (!user) throw new MockError(401, 'INVALID_CHALLENGE', 'This sign-in attempt has expired. Please sign in again.');
    sessionUserId = user.id;
    user.lastLoginAt = nowIso();
    audit('USER_LOGIN', { resourceType: 'session', metadata: { mfa: true } });
    return { user: publicUser(user) };
  },

  async logout() {
    await delay(120);
    audit('USER_LOGOUT', { resourceType: 'session' });
    return { ok: true };
  },

  async me() {
    await delay(120);
    const user = getUser(sessionUserId);
    if (!user) throw new MockError(401, 'UNAUTHENTICATED', 'Your session has ended.');
    const role = getRole(user.roleId);
    return {
      user: publicUser(user),
      roles: role ? [{ key: role.key, name: role.name, rank: role.rank }] : [],
      permissions: role ? [...role.permissions] : [],
      features: {
        externalSharing: db.settings.sharing.externalSharingEnabled,
        aiSearch: db.settings.features.aiSearch,
        ocr: db.settings.features.ocr,
      },
      organization: db.organization,
    };
  },

  /** Demo affordance: impersonate another user to show permission-aware UI. */
  async switchUser(userId) {
    await delay(200);
    const user = getUser(userId);
    if (!user) throw new MockError(404, 'NOT_FOUND', 'User not found.');
    sessionUserId = user.id;
    return this.me();
  },

  /* -------------------------------------------------------------- folders */
  async getFolder(folderId) {
    await delay(100);
    const user = getUser(sessionUserId);
    if (!folderId) {
      return { folder: null, breadcrumbs: [{ id: null, name: 'Home' }] };
    }
    const folder = byId(db.folders, folderId);
    if (!folder || folder.deletedAt) throw new MockError(404, 'NOT_FOUND', 'Folder not found.');
    const caps = capabilitiesForFolder(user, folder);
    if (!caps.includes('view')) throw new MockError(404, 'NOT_FOUND', 'Folder not found.');
    return {
      folder: decorateFolder(folder, user),
      breadcrumbs: [{ id: null, name: 'Home' }, ...folderPath(folder.id).map((f) => ({ id: f.id, name: f.name }))],
    };
  },

  async listFolderTree() {
    await delay(120);
    const user = getUser(sessionUserId);
    return db.folders
      .filter((f) => !f.deletedAt && capabilitiesForFolder(user, f).includes('view'))
      .map((f) => ({ id: f.id, name: f.name, parentId: f.parentId, departmentId: f.departmentId }));
  },

  async createFolder({ name, parentId, departmentId }) {
    await delay(260);
    const user = getUser(sessionUserId);
    requirePermission(user, 'FOLDER_CREATE');
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new MockError(400, 'VALIDATION', 'Folder name is required.');
    if (/[\\/:*?"<>|]/.test(trimmed)) {
      throw new MockError(400, 'VALIDATION', 'A folder name cannot contain \\ / : * ? " < > |');
    }
    const siblings = db.folders.filter((f) => f.parentId === (parentId || null) && !f.deletedAt);
    if (siblings.some((f) => f.name.toLowerCase() === trimmed.toLowerCase())) {
      throw new MockError(409, 'CONFLICT', `A folder named "${trimmed}" already exists here.`);
    }
    const parent = parentId ? byId(db.folders, parentId) : null;
    const folder = {
      id: nextId('fld'),
      name: trimmed,
      parentId: parentId || null,
      departmentId: departmentId || parent?.departmentId || user.departmentId,
      ownerId: user.id,
      type: 'folder',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      deletedAt: null,
    };
    db.folders.push(folder);
    audit('FOLDER_CREATE', { resourceType: 'folder', resourceId: folder.id, resourceName: folder.name });
    return decorateFolder(folder, user);
  },

  async renameFolder(folderId, name) {
    await delay(200);
    const user = getUser(sessionUserId);
    const folder = byId(db.folders, folderId);
    if (!folder) throw new MockError(404, 'NOT_FOUND', 'Folder not found.');
    if (!capabilitiesForFolder(user, folder).includes('edit')) {
      throw new MockError(403, 'FORBIDDEN', 'You do not have permission to rename this folder.');
    }
    const from = folder.name;
    folder.name = String(name).trim();
    folder.updatedAt = nowIso();
    audit('FOLDER_RENAME', { resourceType: 'folder', resourceId: folder.id, resourceName: folder.name, metadata: { from, to: folder.name } });
    return decorateFolder(folder, user);
  },

  async deleteFolder(folderId) {
    await delay(240);
    const user = getUser(sessionUserId);
    requirePermission(user, 'FOLDER_DELETE');
    const folder = byId(db.folders, folderId);
    if (!folder) throw new MockError(404, 'NOT_FOUND', 'Folder not found.');
    if (!capabilitiesForFolder(user, folder).includes('delete')) {
      throw new MockError(403, 'FORBIDDEN', 'You do not have permission to delete this folder.');
    }
    const stack = [folder.id];
    while (stack.length) {
      const id = stack.pop();
      db.folders.filter((f) => f.parentId === id).forEach((f) => stack.push(f.id));
      const f = byId(db.folders, id);
      if (f) {
        f.deletedAt = nowIso();
        f.deletedBy = user.id;
      }
      db.documents
        .filter((d) => d.folderId === id && !d.deletedAt)
        .forEach((d) => {
          d.deletedAt = nowIso();
          d.deletedBy = user.id;
          d.purgeAfter = new Date(Date.now() + db.settings.retention.trashRetentionDays * 86400000).toISOString();
        });
    }
    audit('FOLDER_DELETE', { resourceType: 'folder', resourceId: folder.id, resourceName: folder.name });
    return { ok: true };
  },

  /* ------------------------------------------------------------ documents */
  async listItems({ folderId = null, scope = 'folder', sort = 'name:asc', query = '', typeGroup = null, departmentId = null } = {}) {
    await delay(180);
    const user = getUser(sessionUserId);
    let folderItems = [];
    let docs = [];

    if (scope === 'folder') {
      folderItems = db.folders
        .filter((f) => !f.deletedAt && f.parentId === folderId)
        .filter((f) => capabilitiesForFolder(user, f).includes('view'))
        .map((f) => decorateFolder(f, user));
      docs = db.documents.filter((d) => !d.deletedAt && d.folderId === folderId && canView(user, d));
    } else if (scope === 'mine') {
      docs = db.documents.filter((d) => !d.deletedAt && d.ownerId === user.id);
    } else if (scope === 'shared') {
      docs = db.documents.filter((d) => {
        if (d.deletedAt || d.ownerId === user.id) return false;
        return shareGrantsFor(user, d.id).length > 0 && canView(user, d);
      });
    } else if (scope === 'favorites') {
      const favDocIds = new Set(db.favorites.filter((f) => f.userId === user.id && f.resourceType === 'document').map((f) => f.resourceId));
      const favFolderIds = new Set(db.favorites.filter((f) => f.userId === user.id && f.resourceType === 'folder').map((f) => f.resourceId));
      docs = db.documents.filter((d) => !d.deletedAt && favDocIds.has(d.id) && canView(user, d));
      folderItems = db.folders
        .filter((f) => !f.deletedAt && favFolderIds.has(f.id) && capabilitiesForFolder(user, f).includes('view'))
        .map((f) => decorateFolder(f, user));
    } else if (scope === 'recent') {
      const recent = db.recent.filter((r) => r.userId === user.id).sort((a, b) => new Date(b.lastViewedAt) - new Date(a.lastViewedAt));
      docs = recent
        .map((r) => db.documents.find((d) => d.id === r.documentId))
        .filter((d) => d && !d.deletedAt && canView(user, d));
      const decorated = docs.map((d) => {
        const r = recent.find((x) => x.documentId === d.id);
        return { ...decorateDocument(d, user), lastViewedAt: r?.lastViewedAt };
      });
      return { items: decorated, total: decorated.length };
    } else if (scope === 'trash') {
      docs = db.documents.filter((d) => d.deletedAt && (d.ownerId === user.id || capabilitiesFor(user, d).includes('delete') || permissionsOf(user).has('ADMIN_DOCUMENT_OVERRIDE')));
    } else if (scope === 'department') {
      folderItems = db.folders
        .filter((f) => !f.deletedAt && f.departmentId === departmentId && f.parentId === folderId)
        .filter((f) => capabilitiesForFolder(user, f).includes('view'))
        .map((f) => decorateFolder(f, user));
      docs = db.documents.filter((d) => !d.deletedAt && d.departmentId === departmentId && d.folderId === folderId && canView(user, d));
    }

    let items = [...folderItems, ...docs.map((d) => decorateDocument(d, user))];

    if (query) {
      const q = query.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(q));
    }
    if (typeGroup) {
      items = items.filter((i) => i.type === 'document' && getTypeGroup(i.name) === typeGroup);
    }

    return { items: sortItems(items, sort), total: items.length };
  },

  async getDocument(documentId) {
    await delay(150);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    const caps = capabilitiesFor(user, doc);
    if (!caps.includes('view')) throw new MockError(404, 'NOT_FOUND', 'Document not found.');

    const existing = db.recent.find((r) => r.userId === user.id && r.documentId === doc.id);
    if (existing) existing.lastViewedAt = nowIso();
    else db.recent.push({ userId: user.id, documentId: doc.id, lastViewedAt: nowIso() });

    return { ...decorateDocument(doc, user), ocrText: doc.ocrText };
  },

  async updateDocument(documentId, patch) {
    await delay(240);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'edit');
    const before = { name: doc.name, description: doc.description, tags: [...doc.tags] };
    if (patch.name !== undefined) {
      const trimmed = String(patch.name).trim();
      if (!trimmed) throw new MockError(400, 'VALIDATION', 'A document name is required.');
      doc.name = trimmed;
      doc.extension = getExtension(trimmed) || doc.extension;
    }
    if (patch.description !== undefined) doc.description = patch.description;
    if (patch.tags !== undefined) doc.tags = patch.tags;
    if (patch.isConfidential !== undefined) doc.isConfidential = patch.isConfidential;
    doc.updatedAt = nowIso();
    audit(patch.name !== undefined && patch.name !== before.name ? 'DOCUMENT_RENAME' : 'DOCUMENT_UPDATE', {
      resourceId: doc.id,
      resourceName: doc.name,
      metadata: { from: before.name, to: doc.name },
    });
    return decorateDocument(doc, user);
  },

  async moveDocument(documentId, targetFolderId) {
    await delay(260);
    const user = getUser(sessionUserId);
    requirePermission(user, 'FOLDER_MOVE');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'edit');
    const target = byId(db.folders, targetFolderId);
    if (!target) throw new MockError(404, 'NOT_FOUND', 'Destination folder not found.');
    const from = doc.folderId;
    doc.folderId = target.id;
    doc.departmentId = target.departmentId;
    doc.updatedAt = nowIso();
    audit('DOCUMENT_MOVE', { resourceId: doc.id, resourceName: doc.name, metadata: { from, to: target.id } });
    return decorateDocument(doc, user);
  },

  async copyDocument(documentId, targetFolderId) {
    await delay(300);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_UPLOAD');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'download');
    const target = byId(db.folders, targetFolderId || doc.folderId);
    const dot = doc.name.lastIndexOf('.');
    const copyName = dot > 0 ? `${doc.name.slice(0, dot)} (copy)${doc.name.slice(dot)}` : `${doc.name} (copy)`;
    const copy = {
      ...doc,
      id: nextId('doc'),
      name: copyName,
      folderId: target.id,
      departmentId: target.departmentId,
      ownerId: user.id,
      versionCount: 1,
      currentVersionNo: 1,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      deletedAt: null,
      deletedBy: null,
    };
    db.documents.push(copy);
    db.versions.push({
      id: `${copy.id}-v1`,
      documentId: copy.id,
      versionNo: 1,
      sizeBytes: copy.sizeBytes,
      uploadedBy: user.id,
      changeNote: `Copied from ${doc.name}`,
      createdAt: nowIso(),
    });
    audit('DOCUMENT_COPY', { resourceId: copy.id, resourceName: copy.name, metadata: { source: doc.id } });
    return decorateDocument(copy, user);
  },

  async deleteDocument(documentId) {
    await delay(240);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_DELETE');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'delete');
    doc.deletedAt = nowIso();
    doc.deletedBy = user.id;
    doc.purgeAfter = new Date(Date.now() + db.settings.retention.trashRetentionDays * 86400000).toISOString();
    audit('DOCUMENT_DELETE', { resourceId: doc.id, resourceName: doc.name });
    return { ok: true };
  },

  async restoreDocument(documentId) {
    await delay(240);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    doc.deletedAt = null;
    doc.deletedBy = null;
    doc.purgeAfter = null;
    doc.updatedAt = nowIso();
    audit('DOCUMENT_RESTORE', { resourceId: doc.id, resourceName: doc.name });
    return decorateDocument(doc, user);
  },

  async purgeDocument(documentId) {
    await delay(300);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_DELETE_PERMANENT');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    db.documents = db.documents.filter((d) => d.id !== documentId);
    db.versions = db.versions.filter((v) => v.documentId !== documentId);
    db.shares = db.shares.filter((s) => s.documentId !== documentId);
    db.links = db.links.filter((l) => l.documentId !== documentId);
    audit('DOCUMENT_PURGE', { resourceId: doc.id, resourceName: doc.name });
    return { ok: true };
  },

  async emptyTrash() {
    await delay(400);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_DELETE_PERMANENT');
    const ids = db.documents.filter((d) => d.deletedAt).map((d) => d.id);
    db.documents = db.documents.filter((d) => !d.deletedAt);
    db.versions = db.versions.filter((v) => !ids.includes(v.documentId));
    audit('DOCUMENT_PURGE', { resourceName: `${ids.length} documents`, metadata: { count: ids.length } });
    return { purged: ids.length };
  },

  /** Commit a browser upload. In production this follows the presigned S3 PUT. */
  async commitUpload({ fileName, sizeBytes, folderId, documentId, changeNote }) {
    await delay(220);
    const user = getUser(sessionUserId);

    // A commit against an existing document is a new version, never an overwrite.
    if (documentId) {
      const updated = await this.createVersion(documentId, { sizeBytes, changeNote: changeNote || `Replaced file (${fileName})` });
      const doc = byId(db.documents, documentId);
      doc.name = doc.name; // the display name is unchanged by a replace
      return updated;
    }

    requirePermission(user, 'DOCUMENT_UPLOAD');
    const folder = byId(db.folders, folderId) || byId(db.folders, `fld-${getDepartment(user.departmentId)?.code.toLowerCase()}`);
    const targetFolder = folder || db.folders[0];
    const ext = getExtension(fileName);
    const doc = {
      id: nextId('doc'),
      type: 'document',
      name: fileName,
      folderId: targetFolder.id,
      departmentId: targetFolder.departmentId,
      ownerId: user.id,
      sizeBytes,
      mimeType: ext,
      extension: ext,
      versionCount: 1,
      currentVersionNo: 1,
      tags: [],
      isConfidential: false,
      description: '',
      ocrStatus: TEXT_LAYER_TYPES.includes(ext) || IMAGE_TYPES.includes(ext) ? 'processing' : 'not_applicable',
      ocrText: '',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      deletedAt: null,
      deletedBy: null,
    };
    db.documents.push(doc);
    db.versions.push({
      id: `${doc.id}-v1`,
      documentId: doc.id,
      versionNo: 1,
      sizeBytes,
      uploadedBy: user.id,
      changeNote: 'Initial upload',
      createdAt: nowIso(),
    });
    audit('DOCUMENT_UPLOAD', { resourceId: doc.id, resourceName: doc.name, metadata: { sizeBytes } });

    // Simulate the asynchronous pipeline: text-layer formats are parsed; images go through
    // the built-in OCR engine.
    if (doc.ocrStatus === 'processing') {
      setTimeout(() => {
        doc.ocrStatus = 'completed';
        doc.ocrText = IMAGE_TYPES.includes(ext)
          ? `${doc.name}\n\nText read from the image by Tesseract OCR.`
          : `${doc.name}\n\nExtracted text is available for this document.`;
      }, 6000);
    }
    return decorateDocument(doc, user);
  },

  /* -------------------------------------------------------------- versions */
  async listVersions(documentId) {
    await delay(150);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'view');
    return db.versions
      .filter((v) => v.documentId === documentId)
      .sort((a, b) => b.versionNo - a.versionNo)
      .map((v) => ({
        ...v,
        isCurrent: v.versionNo === doc.currentVersionNo,
        uploadedByUser: publicUser(getUser(v.uploadedBy)),
      }));
  },

  async restoreVersion(documentId, versionId) {
    await delay(360);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'replace');
    const source = db.versions.find((v) => v.id === versionId);
    if (!source) throw new MockError(404, 'NOT_FOUND', 'Version not found.');
    // Restoring copies the old content forward as a NEW version — history is never rewritten.
    const newNo = doc.currentVersionNo + 1;
    db.versions.push({
      id: `${doc.id}-v${newNo}`,
      documentId: doc.id,
      versionNo: newNo,
      sizeBytes: source.sizeBytes,
      uploadedBy: user.id,
      changeNote: `Restored from version ${source.versionNo}`,
      restoredFromVersionId: source.id,
      createdAt: nowIso(),
    });
    doc.currentVersionNo = newNo;
    doc.versionCount = newNo;
    doc.sizeBytes = source.sizeBytes;
    doc.updatedAt = nowIso();
    audit('DOCUMENT_VERSION_RESTORE', { resourceId: doc.id, resourceName: doc.name, metadata: { from: source.versionNo, newVersion: newNo } });
    return decorateDocument(doc, user);
  },

  async createVersion(documentId, { sizeBytes, changeNote }) {
    await delay(400);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'replace');
    const newNo = doc.currentVersionNo + 1;
    db.versions.push({
      id: `${doc.id}-v${newNo}`,
      documentId: doc.id,
      versionNo: newNo,
      sizeBytes: sizeBytes || doc.sizeBytes,
      uploadedBy: user.id,
      changeNote: changeNote || 'New version uploaded',
      createdAt: nowIso(),
    });
    doc.currentVersionNo = newNo;
    doc.versionCount = newNo;
    doc.updatedAt = nowIso();
    audit('DOCUMENT_VERSION_CREATE', { resourceId: doc.id, resourceName: doc.name, metadata: { versionNo: newNo } });
    return decorateDocument(doc, user);
  },

  /* -------------------------------------------------------------- sharing */
  async listShares(documentId) {
    await delay(140);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'view');
    return db.shares
      .filter((s) => s.documentId === documentId && !s.revokedAt)
      .map((s) => ({
        ...s,
        principal:
          s.principalType === 'user'
            ? { ...publicUser(getUser(s.principalId)), type: 'user' }
            : s.principalType === 'department'
              ? { ...getDepartment(s.principalId), type: 'department' }
              : { ...getRole(s.principalId), type: 'role' },
        sharedByUser: publicUser(getUser(s.sharedBy)),
      }));
  },

  async createShare(documentId, { principals, capabilities, expiresAt, message }) {
    await delay(380);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_SHARE');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'share');

    // You cannot grant a capability you do not hold — the server intersects, silently.
    const own = new Set(capabilitiesFor(user, doc));
    const granted = capabilities.filter((c) => own.has(c));
    if (!granted.length) throw new MockError(400, 'VALIDATION', 'You cannot grant any of the selected permissions.');

    const created = principals.map((p) => {
      const share = {
        id: nextId('shr'),
        documentId,
        sharedBy: user.id,
        principalType: p.type,
        principalId: p.id,
        capabilities: granted,
        message: message || '',
        expiresAt: expiresAt || null,
        createdAt: nowIso(),
        revokedAt: null,
      };
      db.shares.push(share);
      if (p.type === 'user') {
        db.notifications.unshift({
          id: nextId('ntf'),
          userId: p.id,
          type: 'share',
          title: `${user.name} shared "${doc.name}" with you`,
          body: message || '',
          resourceType: 'document',
          resourceId: doc.id,
          actorId: user.id,
          readAt: null,
          createdAt: nowIso(),
        });
      }
      return share;
    });
    audit('DOCUMENT_SHARE', {
      resourceId: doc.id,
      resourceName: doc.name,
      metadata: { principals: principals.map((p) => `${p.type}:${p.id}`), capabilities: granted },
    });
    return created;
  },

  async updateShare(shareId, capabilities) {
    await delay(220);
    const user = getUser(sessionUserId);
    const share = byId(db.shares, shareId);
    if (!share) throw new MockError(404, 'NOT_FOUND', 'Share not found.');
    const doc = byId(db.documents, share.documentId);
    requireCapability(user, doc, 'share');
    const own = new Set(capabilitiesFor(user, doc));
    share.capabilities = capabilities.filter((c) => own.has(c));
    audit('DOCUMENT_SHARE_UPDATE', { resourceId: doc.id, resourceName: doc.name });
    return share;
  },

  async revokeShare(shareId) {
    await delay(220);
    const user = getUser(sessionUserId);
    const share = byId(db.shares, shareId);
    if (!share) throw new MockError(404, 'NOT_FOUND', 'Share not found.');
    const doc = byId(db.documents, share.documentId);
    requireCapability(user, doc, 'share');
    share.revokedAt = nowIso();
    audit('DOCUMENT_SHARE_REVOKE', { resourceId: doc.id, resourceName: doc.name });
    return { ok: true };
  },

  /* ------------------------------------------------------- external links */
  async listLinks(documentId) {
    await delay(140);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'view');
    return db.links
      .filter((l) => l.documentId === documentId && !l.revokedAt)
      .map((l) => ({ ...l, password: undefined, createdByUser: publicUser(getUser(l.createdBy)) }));
  },

  async createLink(documentId, { accessType, allowedEmails, capabilities, expiryDays, password }) {
    await delay(420);
    const user = getUser(sessionUserId);
    requirePermission(user, 'DOCUMENT_SHARE_EXTERNAL');
    if (!db.settings.sharing.externalSharingEnabled) {
      throw new MockError(403, 'FEATURE_DISABLED', 'External sharing is disabled for this organisation.');
    }
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'share');
    if (doc.isConfidential) {
      throw new MockError(403, 'CONFIDENTIAL', 'This document is marked confidential and cannot be shared externally.');
    }
    const maxDays = db.settings.sharing.maxLinkExpiryDays;
    const days = Math.min(Number(expiryDays) || 7, maxDays);
    if (db.settings.sharing.requirePasswordForExternal && !password) {
      throw new MockError(400, 'VALIDATION', 'Company policy requires external links to be password protected.');
    }
    const token = Array.from({ length: 16 }, () => 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 56)]).join('');
    const link = {
      id: nextId('lnk'),
      documentId,
      token,
      accessType,
      allowedEmails: allowedEmails || [],
      capabilities: capabilities || ['view'],
      hasPassword: Boolean(password),
      password: password || null,
      expiresAt: new Date(Date.now() + days * 86400000).toISOString(),
      maxDownloads: null,
      downloadCount: 0,
      createdBy: user.id,
      createdAt: nowIso(),
      revokedAt: null,
      lastAccessedAt: null,
    };
    db.links.push(link);
    audit('EXTERNAL_LINK_CREATE', { resourceId: doc.id, resourceName: doc.name, metadata: { accessType, expiryDays: days } });
    return { ...link, password: undefined, url: `${window.location.origin}/s/${token}` };
  },

  async revokeLink(linkId) {
    await delay(220);
    const user = getUser(sessionUserId);
    const link = byId(db.links, linkId);
    if (!link) throw new MockError(404, 'NOT_FOUND', 'Link not found.');
    const doc = byId(db.documents, link.documentId);
    requireCapability(user, doc, 'share');
    link.revokedAt = nowIso();
    audit('EXTERNAL_LINK_REVOKE', { resourceId: doc.id, resourceName: doc.name });
    return { ok: true };
  },

  /** Public, unauthenticated: resolve an external link by token. */
  async resolvePublicLink(token, password) {
    await delay(320);
    const link = db.links.find((l) => l.token === token);
    if (!link || link.revokedAt) return { state: 'expired' };
    if (new Date(link.expiresAt).getTime() < Date.now()) return { state: 'expired' };
    if (link.hasPassword && password !== link.password) {
      return { state: password === undefined ? 'password_required' : 'password_invalid' };
    }
    const doc = byId(db.documents, link.documentId);
    if (!doc || doc.deletedAt) return { state: 'expired' };
    const owner = getUser(doc.ownerId);
    return {
      state: 'valid',
      capabilities: link.capabilities,
      expiresAt: link.expiresAt,
      document: {
        id: doc.id,
        name: doc.name,
        extension: doc.extension,
        sizeBytes: doc.sizeBytes,
        updatedAt: doc.updatedAt,
        ocrText: doc.ocrText,
        sharedBy: owner ? owner.name : 'Inditech',
        organization: db.organization.name,
      },
    };
  },

  /* ------------------------------------------------------------ favorites */
  async toggleFavorite(resourceType, resourceId) {
    await delay(110);
    const user = getUser(sessionUserId);
    const i = db.favorites.findIndex(
      (f) => f.userId === user.id && f.resourceType === resourceType && f.resourceId === resourceId,
    );
    if (i >= 0) {
      db.favorites.splice(i, 1);
      return { isFavorite: false };
    }
    db.favorites.push({ userId: user.id, resourceType, resourceId, createdAt: nowIso() });
    return { isFavorite: true };
  },

  /* --------------------------------------------------------------- search */
  async search({ q = '', departmentId = null, typeGroup = null, ownerId = null, modified = null, tag = null, sort = 'relevance' } = {}) {
    await delay(300);
    const user = getUser(sessionUserId);
    const term = q.trim().toLowerCase();
    const visible = db.documents.filter((d) => !d.deletedAt && canView(user, d));

    const cutoff = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }[modified];
    const cutoffTime = cutoff ? Date.now() - cutoff * 86400000 : null;

    const scored = [];
    visible.forEach((doc) => {
      if (departmentId && doc.departmentId !== departmentId) return;
      if (typeGroup && getTypeGroup(doc.name) !== typeGroup) return;
      if (ownerId && doc.ownerId !== ownerId) return;
      if (tag && !doc.tags.includes(tag)) return;
      if (cutoffTime && new Date(doc.updatedAt).getTime() < cutoffTime) return;

      let score = 0;
      let snippet = '';
      if (term) {
        const name = doc.name.toLowerCase();
        if (name === term) score += 100;
        else if (name.startsWith(term)) score += 60;
        else if (name.includes(term)) score += 40;
        term.split(/\s+/).forEach((word) => {
          if (word.length > 2 && name.includes(word)) score += 12;
        });
        if (doc.tags.some((t) => t.includes(term))) score += 20;
        const body = (doc.ocrText || '').toLowerCase();
        const hit = body.indexOf(term);
        if (hit >= 0) {
          score += 24;
          snippet = makeSnippet(doc.ocrText, hit, term.length);
        } else {
          const words = term.split(/\s+/).filter((w) => w.length > 2);
          const found = words.find((w) => body.includes(w));
          if (found) {
            score += 8;
            snippet = makeSnippet(doc.ocrText, body.indexOf(found), found.length);
          }
        }
        if (score === 0) return;
      } else {
        score = new Date(doc.updatedAt).getTime() / 1e12;
      }
      scored.push({ ...decorateDocument(doc, user), score, snippet, matchedContent: Boolean(snippet) });
    });

    if (sort === 'modified') scored.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    else if (sort === 'name') scored.sort((a, b) => a.name.localeCompare(b.name));
    else scored.sort((a, b) => b.score - a.score);

    // facets computed over the authorized set, before filters — like the real API
    const base = visible;
    const facets = {
      departments: db.departments
        .map((d) => ({ id: d.id, name: d.name, color: d.color, count: base.filter((x) => x.departmentId === d.id).length }))
        .filter((d) => d.count > 0),
      types: ['pdf', 'document', 'spreadsheet', 'presentation', 'image', 'archive']
        .map((key) => ({ key, count: base.filter((x) => getTypeGroup(x.name) === key).length }))
        .filter((t) => t.count > 0),
      owners: db.users
        .map((u) => ({ id: u.id, name: u.name, count: base.filter((x) => x.ownerId === u.id).length }))
        .filter((o) => o.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8),
      tags: [...new Set(base.flatMap((d) => d.tags))]
        .map((t) => ({ key: t, count: base.filter((d) => d.tags.includes(t)).length }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10),
    };

    return { results: scored, total: scored.length, facets };
  },

  async suggest(q) {
    await delay(110);
    const user = getUser(sessionUserId);
    const term = q.trim().toLowerCase();
    if (!term) return { documents: [], folders: [], content: [] };
    const visible = db.documents.filter((d) => !d.deletedAt && canView(user, d));
    return {
      documents: visible
        .filter((d) => d.name.toLowerCase().includes(term))
        .slice(0, 5)
        .map((d) => decorateDocument(d, user)),
      folders: db.folders
        .filter((f) => !f.deletedAt && f.name.toLowerCase().includes(term) && capabilitiesForFolder(user, f).includes('view'))
        .slice(0, 3)
        .map((f) => decorateFolder(f, user)),
      content: visible
        .filter((d) => !d.name.toLowerCase().includes(term) && (d.ocrText || '').toLowerCase().includes(term))
        .slice(0, 4)
        .map((d) => decorateDocument(d, user)),
    };
  },

  /**
   * AI search — extracts intent from the question, then runs the ordinary authorized
   * search. Results can never exceed the caller's permissions.
   */
  async aiSearch(question) {
    await delay(900);
    const user = getUser(sessionUserId);
    requirePermission(user, 'SEARCH_AI');
    const text = question.toLowerCase();

    const stop = new Set(['find', 'all', 'the', 'show', 'me', 'any', 'related', 'to', 'for', 'about', 'with', 'in', 'of', 'and', 'documents', 'document', 'files', 'file', 'get', 'list', 'from', 'last', 'this', 'that', 'a', 'an', 'give']);
    const keywords = text
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !stop.has(w));

    const typeHints = [
      { match: ['agreement', 'agreements', 'contract', 'contracts'], tag: 'agreement', label: 'agreements' },
      { match: ['proposal', 'proposals'], tag: 'proposal', label: 'proposals' },
      { match: ['invoice', 'invoices'], tag: 'invoice', label: 'invoices' },
      { match: ['policy', 'policies'], tag: 'policy', label: 'policies' },
      { match: ['report', 'reports'], tag: 'report', label: 'reports' },
    ];
    const hint = typeHints.find((h) => h.match.some((m) => text.includes(m)));
    const dept = db.departments.find((d) => text.includes(d.name.toLowerCase()));
    const subject = keywords.filter((k) => !hint?.match.includes(k) && !db.departments.some((d) => d.name.toLowerCase().includes(k)));

    const visible = db.documents.filter((d) => !d.deletedAt && canView(user, d));
    const results = [];
    visible.forEach((doc) => {
      if (dept && doc.departmentId !== dept.id) return;
      let score = 0;
      const haystack = `${doc.name} ${doc.tags.join(' ')} ${doc.ocrText || ''}`.toLowerCase();
      if (hint) {
        if (doc.tags.includes(hint.tag)) score += 45;
        else if (haystack.includes(hint.tag)) score += 18;
      }
      subject.forEach((word) => {
        if (doc.name.toLowerCase().includes(word)) score += 34;
        else if (doc.tags.includes(word)) score += 26;
        else if (haystack.includes(word)) score += 11;
      });
      if (score <= 0) return;
      const reasonBits = [];
      if (hint && doc.tags.includes(hint.tag)) reasonBits.push(`tagged as ${hint.tag}`);
      const named = subject.find((w) => doc.name.toLowerCase().includes(w));
      if (named) reasonBits.push(`title mentions "${named}"`);
      else {
        const inBody = subject.find((w) => (doc.ocrText || '').toLowerCase().includes(w));
        if (inBody) reasonBits.push(`content mentions "${inBody}"`);
      }
      results.push({
        ...decorateDocument(doc, user),
        score,
        relevance: score >= 60 ? 'High' : score >= 30 ? 'Medium' : 'Low',
        reason: reasonBits.length ? reasonBits.join(' and ') : 'related content match',
      });
    });
    results.sort((a, b) => b.score - a.score);

    const filters = [];
    if (hint) filters.push({ key: 'type', label: hint.label });
    if (dept) filters.push({ key: 'department', label: dept.name });
    subject.slice(0, 3).forEach((s) => filters.push({ key: 'keyword', label: s }));

    return {
      interpretation: `Looking for ${hint ? hint.label : 'documents'}${subject.length ? ` mentioning “${subject.join(' ')}”` : ''}${dept ? ` in ${dept.name}` : ''}.`,
      filters,
      results: results.slice(0, 12),
      total: results.length,
    };
  },

  /* -------------------------------------------------------------- activity */
  async documentActivity(documentId) {
    await delay(160);
    const user = getUser(sessionUserId);
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    requireCapability(user, doc, 'view');
    return db.audit
      .filter((a) => a.resourceId === documentId)
      .slice(0, 40)
      .map((a) => ({ ...a, actor: publicUser(getUser(a.actorId)) }));
  },

  /* --------------------------------------------------------- notifications */
  async listNotifications() {
    await delay(130);
    const user = getUser(sessionUserId);
    return db.notifications
      .filter((n) => n.userId === user.id)
      .map((n) => ({ ...n, actor: n.actorId ? publicUser(getUser(n.actorId)) : null }));
  },

  async markNotificationRead(id) {
    await delay(80);
    const n = byId(db.notifications, id);
    if (n) n.readAt = nowIso();
    return { ok: true };
  },

  async markAllNotificationsRead() {
    await delay(120);
    const user = getUser(sessionUserId);
    db.notifications.filter((n) => n.userId === user.id && !n.readAt).forEach((n) => {
      n.readAt = nowIso();
    });
    return { ok: true };
  },

  /* ---------------------------------------------------------- dashboard */
  async dashboard() {
    await delay(240);
    const user = getUser(sessionUserId);
    const mine = db.documents.filter((d) => !d.deletedAt && d.ownerId === user.id);
    const sharedWithMe = db.documents.filter(
      (d) => !d.deletedAt && d.ownerId !== user.id && shareGrantsFor(user, d.id).length > 0 && canView(user, d),
    );
    const recent = db.recent
      .filter((r) => r.userId === user.id)
      .sort((a, b) => new Date(b.lastViewedAt) - new Date(a.lastViewedAt));
    const favs = db.favorites.filter((f) => f.userId === user.id);

    const recentDocs = db.documents
      .filter((d) => !d.deletedAt && canView(user, d))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 8)
      .map((d) => decorateDocument(d, user));

    const recentShares = db.shares
      .filter((s) => {
        if (s.revokedAt) return false;
        const matches =
          (s.principalType === 'user' && s.principalId === user.id) ||
          (s.principalType === 'department' && s.principalId === user.departmentId);
        if (!matches) return false;
        const doc = byId(db.documents, s.documentId);
        return doc && !doc.deletedAt && canView(user, doc);
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5)
      .map((s) => ({
        ...s,
        document: decorateDocument(byId(db.documents, s.documentId), user),
        sharedByUser: publicUser(getUser(s.sharedBy)),
      }));

    const processing = db.documents.filter((d) => d.ownerId === user.id && d.ocrStatus === 'processing').length;

    return {
      stats: {
        myDocuments: mine.length,
        sharedWithMe: sharedWithMe.length,
        recent: recent.length,
        favorites: favs.length,
      },
      recentDocuments: recentDocs,
      recentShares,
      processingCount: processing,
    };
  },

  /* ---------------------------------------------------------- departments */
  async listDepartments() {
    await delay(120);
    const user = getUser(sessionUserId);
    return db.departments.map((d) => ({
      ...d,
      userCount: db.users.filter((u) => u.departmentId === d.id && u.status !== 'deactivated').length,
      documentCount: db.documents.filter((x) => x.departmentId === d.id && !x.deletedAt).length,
      storageBytes: db.documents.filter((x) => x.departmentId === d.id && !x.deletedAt).reduce((s, x) => s + x.sizeBytes, 0),
      rootFolderId: db.folders.find((f) => f.departmentId === d.id && !f.parentId)?.id || null,
      isMine: d.id === user.departmentId,
    }));
  },

  async createDepartment({ name, code, description, color }) {
    await delay(300);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_DEPARTMENT_MANAGE');
    if (!name?.trim()) throw new MockError(400, 'VALIDATION', 'Department name is required.');
    if (db.departments.some((d) => d.code.toLowerCase() === String(code).toLowerCase())) {
      throw new MockError(409, 'CONFLICT', 'That department code is already in use.');
    }
    const dept = {
      id: nextId('dep'),
      name: name.trim(),
      code: String(code || name).toUpperCase().slice(0, 6),
      description: description || '',
      color: color || '#1B4DB1',
      status: 'active',
      createdAt: nowIso(),
    };
    db.departments.push(dept);
    db.folders.push({
      id: nextId('fld'),
      name: dept.name,
      departmentId: dept.id,
      parentId: null,
      ownerId: user.id,
      type: 'folder',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      deletedAt: null,
    });
    audit('DEPARTMENT_CREATE', { resourceType: 'department', resourceId: dept.id, resourceName: dept.name });
    return dept;
  },

  async updateDepartment(id, patch) {
    await delay(260);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_DEPARTMENT_MANAGE');
    const dept = byId(db.departments, id);
    if (!dept) throw new MockError(404, 'NOT_FOUND', 'Department not found.');
    Object.assign(dept, patch);
    audit('DEPARTMENT_UPDATE', { resourceType: 'department', resourceId: dept.id, resourceName: dept.name });
    return dept;
  },

  /* ---------------------------------------------------------------- users */
  async listUsers() {
    await delay(180);
    return db.users.map(publicUser);
  },

  async getUserDetail(id) {
    await delay(180);
    const user = getUser(id);
    if (!user) throw new MockError(404, 'NOT_FOUND', 'User not found.');
    const role = getRole(user.roleId);
    return {
      ...publicUser(user),
      permissions: role ? [...role.permissions] : [],
      documentCount: db.documents.filter((d) => d.ownerId === id && !d.deletedAt).length,
      recentActivity: db.audit.filter((a) => a.actorId === id).slice(0, 12),
    };
  },

  async createUser({ name, email, departmentId, roleId }) {
    await delay(360);
    const actor = getUser(sessionUserId);
    requirePermission(actor, 'ADMIN_USER_MANAGE');
    if (!name?.trim() || !email?.trim()) throw new MockError(400, 'VALIDATION', 'Name and email are required.');
    if (db.users.some((u) => u.email.toLowerCase() === email.toLowerCase())) {
      throw new MockError(409, 'CONFLICT', 'A user with that email already exists.');
    }
    const actorRole = getRole(actor.roleId);
    const targetRole = getRole(roleId);
    if (targetRole && actorRole && targetRole.rank < actorRole.rank) {
      throw new MockError(403, 'FORBIDDEN', 'You cannot assign a role with more privileges than your own.');
    }
    const user = {
      id: nextId('usr'),
      name: name.trim(),
      email: email.trim().toLowerCase(),
      jobTitle: '',
      departmentId,
      roleId,
      status: 'invited',
      mfaEnabled: false,
      lastLoginAt: null,
      createdAt: nowIso(),
    };
    db.users.push(user);
    audit('USER_CREATED', { resourceType: 'user', resourceId: user.id, resourceName: user.name });
    return publicUser(user);
  },

  async updateUser(id, patch) {
    await delay(300);
    const actor = getUser(sessionUserId);
    requirePermission(actor, 'ADMIN_USER_MANAGE');
    const user = getUser(id);
    if (!user) throw new MockError(404, 'NOT_FOUND', 'User not found.');
    if (id === actor.id && (patch.roleId || patch.status)) {
      throw new MockError(403, 'FORBIDDEN', 'You cannot change your own role or status. Another administrator must do this.');
    }
    if (patch.roleId) {
      const actorRole = getRole(actor.roleId);
      const targetRole = getRole(patch.roleId);
      if (targetRole && actorRole && targetRole.rank < actorRole.rank) {
        throw new MockError(403, 'FORBIDDEN', 'You cannot assign a role with more privileges than your own.');
      }
      audit('ROLE_CHANGE', { resourceType: 'user', resourceId: id, resourceName: user.name, metadata: { from: user.roleId, to: patch.roleId } });
    }
    Object.assign(user, patch);
    audit('USER_UPDATED', { resourceType: 'user', resourceId: id, resourceName: user.name });
    return publicUser(user);
  },

  /* ---------------------------------------------------------------- roles */
  async listRoles() {
    await delay(140);
    return db.roles.map((r) => ({
      ...r,
      userCount: db.users.filter((u) => u.roleId === r.id && u.status !== 'deactivated').length,
    }));
  },

  async listPermissions() {
    await delay(110);
    return db.permissions;
  },

  async updateRolePermissions(roleId, permissionKeys) {
    await delay(360);
    const actor = getUser(sessionUserId);
    requirePermission(actor, 'ADMIN_ROLE_MANAGE');
    const role = byId(db.roles, roleId);
    if (!role) throw new MockError(404, 'NOT_FOUND', 'Role not found.');
    const before = [...role.permissions];
    role.permissions = [...permissionKeys];
    const added = permissionKeys.filter((k) => !before.includes(k));
    const removed = before.filter((k) => !permissionKeys.includes(k));
    audit('ROLE_PERMISSION_CHANGE', {
      resourceType: 'role',
      resourceId: role.id,
      resourceName: role.name,
      metadata: { added, removed },
    });
    return { ...role, userCount: db.users.filter((u) => u.roleId === role.id).length };
  },

  /* ----------------------------------------------------------- audit logs */
  async listAuditLogs({ actorId = null, action = null, departmentId = null, from = null, to = null, page = 1, pageSize = 25 } = {}) {
    await delay(260);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_AUDIT_VIEW');
    let rows = db.audit;
    if (actorId) rows = rows.filter((r) => r.actorId === actorId);
    if (action) rows = rows.filter((r) => r.action === action);
    if (departmentId) rows = rows.filter((r) => r.departmentId === departmentId);
    if (from) rows = rows.filter((r) => new Date(r.createdAt) >= new Date(from));
    if (to) rows = rows.filter((r) => new Date(r.createdAt) <= new Date(to));
    const total = rows.length;
    const start = (page - 1) * pageSize;
    return {
      rows: rows.slice(start, start + pageSize).map((r) => ({ ...r, actor: publicUser(getUser(r.actorId)) })),
      total,
      page,
      pageSize,
      actions: [...new Set(db.audit.map((r) => r.action))].sort(),
    };
  },

  /* ------------------------------------------------------- admin overview */
  async adminStats() {
    await delay(300);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_STORAGE_VIEW');
    const active = db.documents.filter((d) => !d.deletedAt);
    const totalBytes = active.reduce((s, d) => s + d.sizeBytes, 0);
    return {
      users: db.users.filter((u) => u.status === 'active').length,
      departments: db.departments.filter((d) => d.status === 'active').length,
      documents: active.length,
      storageBytes: totalBytes,
      quotaBytes: db.settings.storage.totalQuotaBytes,
      byDepartment: db.departments
        .map((d) => ({
          id: d.id,
          name: d.name,
          color: d.color,
          bytes: active.filter((x) => x.departmentId === d.id).reduce((s, x) => s + x.sizeBytes, 0),
          documents: active.filter((x) => x.departmentId === d.id).length,
        }))
        .sort((a, b) => b.bytes - a.bytes),
      largest: [...active].sort((a, b) => b.sizeBytes - a.sizeBytes).slice(0, 6).map((d) => decorateDocument(d, user)),
      ocr: {
        completed: active.filter((d) => d.ocrStatus === 'completed').length,
        processing: active.filter((d) => d.ocrStatus === 'processing').length,
        failed: active.filter((d) => d.ocrStatus === 'failed').length,
        needsOcr: active.filter((d) => d.ocrStatus === 'needs_ocr').length,
        notApplicable: active.filter((d) => d.ocrStatus === 'not_applicable').length,
        engine: 'tesseract',
        languages: 'eng+ara',
      },
      failedDocuments: active.filter((d) => d.ocrStatus === 'failed').map((d) => decorateDocument(d, user)),
      needsOcrDocuments: active.filter((d) => d.ocrStatus === 'needs_ocr').map((d) => decorateDocument(d, user)),
      recentActivity: db.audit.slice(0, 10).map((a) => ({ ...a, actor: publicUser(getUser(a.actorId)) })),
      securityEvents: db.audit.filter((a) => a.result === 'denied' || a.action === 'PERMISSION_DENIED').slice(0, 6),
      recentUploads: [...active]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5)
        .map((d) => decorateDocument(d, user)),
    };
  },

  async retryOcr(documentId) {
    await delay(400);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_STORAGE_VIEW');
    const doc = byId(db.documents, documentId);
    if (!doc) throw new MockError(404, 'NOT_FOUND', 'Document not found.');
    doc.ocrStatus = 'processing';
    setTimeout(() => {
      doc.ocrStatus = 'completed';
      if (!doc.ocrText) doc.ocrText = `${doc.name}\n\nText read by Tesseract OCR on retry.`;
    }, 5000);
    audit('OCR_RETRY', { resourceId: doc.id, resourceName: doc.name });
    return { ok: true };
  },

  /* ------------------------------------------------------------- settings */
  async getSettings() {
    await delay(150);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_SETTINGS_MANAGE');
    return JSON.parse(JSON.stringify(db.settings));
  },

  async updateSettings(patch) {
    await delay(320);
    const user = getUser(sessionUserId);
    requirePermission(user, 'ADMIN_SETTINGS_MANAGE');
    Object.entries(patch).forEach(([section, values]) => {
      db.settings[section] = { ...db.settings[section], ...values };
    });
    audit('SETTINGS_CHANGE', { resourceType: 'settings', resourceName: Object.keys(patch).join(', '), metadata: patch });
    return JSON.parse(JSON.stringify(db.settings));
  },

  async updateProfile(patch) {
    await delay(260);
    const user = getUser(sessionUserId);
    if (patch.name) user.name = patch.name;
    if (patch.jobTitle !== undefined) user.jobTitle = patch.jobTitle;
    if (patch.mfaEnabled !== undefined) user.mfaEnabled = patch.mfaEnabled;
    if (patch.preferences) user.preferences = { ...(user.preferences || {}), ...patch.preferences };
    return publicUser(user);
  },

  async listSessions() {
    await delay(120);
    return [
      { id: 'ses-current', userAgent: 'Chrome 129 / Windows', ipAddress: '10.18.1.24', createdAt: nowIso(), current: true },
      { id: 'ses-phone', userAgent: 'Safari 18 / iPhone', ipAddress: '10.18.4.91', createdAt: hoursAgo(2), current: false },
    ];
  },

  async revokeSession() {
    await delay(150);
    audit('USER_LOGOUT', { resourceType: 'session', metadata: { revoked: true } });
    return { ok: true };
  },

  async changePassword({ currentPassword, newPassword }) {
    await delay(320);
    if (!currentPassword) throw new MockError(400, 'VALIDATION', 'Enter your current password.');
    if (String(newPassword).length < 12) throw new MockError(400, 'VALIDATION', 'Use at least 12 characters.');
    audit('PASSWORD_CHANGE', { resourceType: 'session' });
    return { ok: true };
  },

  async moveFolder(folderId, targetParentId) {
    await delay(260);
    const user = getUser(sessionUserId);
    requirePermission(user, 'FOLDER_MOVE');
    const folder = byId(db.folders, folderId);
    if (!folder) throw new MockError(404, 'NOT_FOUND', 'Folder not found.');
    if (!capabilitiesForFolder(user, folder).includes('edit')) {
      throw new MockError(403, 'FORBIDDEN', 'You do not have permission to move this folder.');
    }
    // A folder may not be moved into itself or any descendant.
    let cursor = targetParentId ? byId(db.folders, targetParentId) : null;
    while (cursor) {
      if (cursor.id === folder.id) throw new MockError(400, 'VALIDATION', 'A folder cannot be moved into itself or one of its sub-folders.');
      cursor = cursor.parentId ? byId(db.folders, cursor.parentId) : null;
    }
    const target = targetParentId ? byId(db.folders, targetParentId) : null;
    folder.parentId = target ? target.id : null;
    if (target) folder.departmentId = target.departmentId;
    folder.updatedAt = nowIso();
    audit('FOLDER_MOVE', { resourceType: 'folder', resourceId: folder.id, resourceName: folder.name, metadata: { to: targetParentId } });
    return decorateFolder(folder, user);
  },

  async createRole({ name, description, permissions: keys = [], copyFromRoleId }) {
    await delay(300);
    const actor = getUser(sessionUserId);
    requirePermission(actor, 'ADMIN_ROLE_MANAGE');
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new MockError(400, 'VALIDATION', 'A role name is required.');
    if (db.roles.some((r) => r.name.toLowerCase() === trimmed.toLowerCase())) {
      throw new MockError(409, 'CONFLICT', 'A role with that name already exists.');
    }
    const template = copyFromRoleId ? byId(db.roles, copyFromRoleId) : null;
    const granted = (template ? template.permissions : keys).filter((k) => db.permissions.some((p) => p.key === k));
    // A new role is never more privileged than the role that created it.
    const actorRole = getRole(actor.roleId);
    const role = {
      id: nextId('role'),
      key: trimmed.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, ''),
      name: trimmed,
      description: description || '',
      isSystem: false,
      rank: Math.max(actorRole?.rank ?? 1, ...db.roles.map((r) => r.rank)) + 1,
      permissions: granted.filter((k) => actor && permissionsOf(actor).has(k)),
    };
    db.roles.push(role);
    audit('ROLE_CREATE', { resourceType: 'role', resourceId: role.id, resourceName: role.name, metadata: { permissions: role.permissions } });
    return { ...role, userCount: 0 };
  },

  async deleteRole(roleId) {
    await delay(260);
    const actor = getUser(sessionUserId);
    requirePermission(actor, 'ADMIN_ROLE_MANAGE');
    const role = byId(db.roles, roleId);
    if (!role) throw new MockError(404, 'NOT_FOUND', 'Role not found.');
    if (role.isSystem) throw new MockError(403, 'FORBIDDEN', 'System roles cannot be deleted.');
    if (db.users.some((u) => u.roleId === roleId)) {
      throw new MockError(409, 'CONFLICT', 'Reassign the users holding this role before deleting it.');
    }
    db.roles = db.roles.filter((r) => r.id !== roleId);
    audit('ROLE_DELETE', { resourceType: 'role', resourceId: role.id, resourceName: role.name });
    return { ok: true };
  },

  /* ----------------------------------------------------- principal picker */
  async searchPrincipals(q) {
    await delay(140);
    const term = String(q || '').toLowerCase();
    const user = getUser(sessionUserId);
    const matchedUsers = db.users
      .filter((u) => u.id !== user.id && u.status === 'active')
      .filter((u) => !term || u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term))
      .slice(0, 6)
      .map((u) => ({ type: 'user', id: u.id, name: u.name, subtitle: getDepartment(u.departmentId)?.name || '', email: u.email }));
    const matchedDepts = db.departments
      .filter((d) => !term || d.name.toLowerCase().includes(term))
      .slice(0, 4)
      .map((d) => ({ type: 'department', id: d.id, name: `${d.name} Department`, subtitle: `${db.users.filter((u) => u.departmentId === d.id).length} members`, color: d.color }));
    return [...matchedUsers, ...matchedDepts];
  },

  /** Demo helper — exposes the seeded accounts on the login screen. */
  demoAccounts() {
    return db.users
      .filter((u) => ['usr-vaisakh', 'usr-sareesh', 'usr-mohsin', 'usr-meera'].includes(u.id))
      .map((u) => ({ ...publicUser(u), roleName: getRole(u.roleId)?.name }));
  },
};

export { MockError, escapeRegExp };
export default mockApi;
